/**
 * Array Credit Pull — approval workflow + CreditReport__c writeback service.
 *
 * Each Array pull becomes a distinct CreditReport__c record, capturing the
 * approval state machine AND the Array vendor metadata once the pull succeeds.
 * That single source of truth (a) matches the per-record pattern the Credit
 * Reports list below the panel already uses, and (b) is shared state visible
 * to all users who load the page — unlike the mockDataManager approval store
 * which lives in per-browser localStorage.
 *
 * State machine on ArrayRequestStatus__c:
 *   pending_approval → approved  → completed   (happy path)
 *                    → rejected                 (approver said no)
 *                    → recalled                 (requester withdrew)
 *
 * Approvers (Kathleen, Melanie, Dr. Cris — credit block team leads) bypass
 * the state machine entirely: their pull creates a record already stamped
 * `approved` with approver = their own identity.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { getRecordsForObject } from '../recordStorageService';
import { getCreditReportClientRelKey } from '../../config/gobodoAccountRelationshipKeys';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CREDIT_REPORT_OBJECT_KEY = `CreditReport${CUSTOM_SUFFIX}`;
const f = (name: string) => `${name}${CUSTOM_SUFFIX}`;

// Standard SF-sourced CR field names. Set on every Array record so it shows
// up in the existing useCreditReportRecords list with populated columns,
// matching the legacy RDS-synced records.
const FIELD_PULL_DATE = 'Credit_Report_Pull_Date__c';
const FIELD_STATUS = 'Credit_Report_Status__c';
const FIELD_PRODUCT_CODE = 'Credit_Report_Product_Code__c';
const FIELD_REPORT_IDENTIFIER = 'CreditReportIdentifier__c';
const FIELD_BUREAU = 'CreditBureau__c';
const FIELD_NAME = 'Name__c';
const FIELD_TOTAL_FILES = 'Total_Files_Attached__c';

const ARRAY_PRODUCT_CODE = 'CR_ARRAY';
// Array is a 3-bureau pull; surfaces as "tri_merge" in the existing UI bureau
// normalizer (src/services/queries/creditReportQueries.ts:53).
const ARRAY_BUREAU = 'tri_merge';

export type ArrayRequestStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'recalled'
  | 'completed';

export interface ApprovalHistoryEntry {
  id: string;
  action: string; // e.g. 'submitted', 'approved', 'rejected', 'recalled', 'pull_completed'
  approvedBy: string;
  approvalDate: string;
  notes?: string;
}

export interface ArrayCreditReportRecord {
  id: string;
  /** Gobodo object key (used for graph edges; auto-generated by createObject). */
  key: string;
  clientId: string;
  arrayClientKey: string;
  requestStatus: ArrayRequestStatus;
  requestedBy: string;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  arrayOrderId?: string;
  arrayReportKey?: string;
  arrayFirstOrderedAt?: string;
  arrayPdfStatus?: string;
  arrayJsonStatus?: string;
  arrayPdfFileId?: string;
  arrayJsonFileId?: string;
  approvalHistory: ApprovalHistoryEntry[];
}

interface GobodoRecord {
  id: number;
  typeid?: number;
  label?: string;
  properties?: Record<string, unknown>;
  data?: Record<string, unknown>;
  [k: string]: unknown;
}

function readProp<T = string>(rec: GobodoRecord, key: string): T | undefined {
  const source = (rec.properties || rec.data || (rec as Record<string, unknown>)) as Record<string, unknown>;
  const v = source[key];
  return v == null || v === '' ? undefined : (v as T);
}

function parseApprovalHistory(raw: unknown): ApprovalHistoryEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as ApprovalHistoryEntry[];
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ApprovalHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function mapRecord(raw: GobodoRecord): ArrayCreditReportRecord {
  const clientId = String(
    readProp(raw, f('ClientRelField')) ??
      readProp(raw, 'ClientRelField__c') ??
      readProp(raw, 'Client__c') ??
      readProp(raw, 'AccountRelField__c') ??
      readProp(raw, 'AccountId__c') ??
      '',
  );
  return {
    id: String(raw.id),
    key: String((raw as Record<string, unknown>).key ?? ''),
    clientId,
    arrayClientKey: String(readProp(raw, 'ArrayClientKey__c') ?? readProp(raw, 'arrayClientKey') ?? ''),
    requestStatus: (readProp<string>(raw, f('ArrayRequestStatus')) as ArrayRequestStatus) ?? 'pending_approval',
    requestedBy: String(readProp(raw, f('ArrayRequestedBy')) ?? ''),
    requestedAt: String(readProp(raw, f('ArrayRequestedAt')) ?? ''),
    approvedBy: readProp<string>(raw, f('ArrayApprovedBy')),
    approvedAt: readProp<string>(raw, f('ArrayApprovedAt')),
    rejectionReason: readProp<string>(raw, f('ArrayRejectionReason')),
    arrayOrderId: readProp<string>(raw, f('ArrayOrderId')),
    arrayReportKey: readProp<string>(raw, f('ArrayReportKey')),
    arrayFirstOrderedAt: readProp<string>(raw, f('ArrayFirstOrderedAt')),
    arrayPdfStatus: readProp<string>(raw, f('ArrayPdfStatus')),
    arrayJsonStatus: readProp<string>(raw, f('ArrayJsonStatus')),
    arrayPdfFileId: readProp<string>(raw, f('ArrayPdfFileId')),
    arrayJsonFileId: readProp<string>(raw, f('ArrayJsonFileId')),
    approvalHistory: parseApprovalHistory(readProp(raw, f('ArrayApprovalHistory'))),
  };
}

async function getTypeId(): Promise<number> {
  const client = getGobodoClient();
  const api = new GobodoApi.GraphObjectTypesApi(client);
  const objectType = await retryWithBackoff(
    () => api.getObjectTypeByKey(CREDIT_REPORT_OBJECT_KEY, { maxFields: 0 }),
    { maxRetries: 3 },
  );
  if (objectType?.id == null) {
    throw new Error(`CreditReport object type "${CREDIT_REPORT_OBJECT_KEY}" not found in Gobodo`);
  }
  return objectType.id as number;
}

/** Create a new Array credit-report record. */
export interface CreateRequestInput {
  clientId: string;
  clientName: string;
  arrayClientKey: string;
  requestedBy: string; // user identifier (email or keycloak sub)
  /** If the caller is an approver, pre-stamp as approved by passing `approvedBy`. */
  approvedBy?: string;
}

/** Append an entry to a JSON-stringified approval history field. */
function appendApprovalHistory(
  existingJson: string | undefined,
  entry: ApprovalHistoryEntry,
): string {
  const list = parseApprovalHistory(existingJson);
  return JSON.stringify([...list, entry]);
}

/**
 * Create the Client → CreditReport graph edge so the new record shows up in
 * the existing useCreditReportRecords traversal query (the same query the
 * 19 SF-synced legacy records show up in). Mirrors the L2 scheduler pattern
 * in lambdas/rds-to-gobodo-scheduler/src/mapRelationship.ts:39 — same key
 * shape, same relationshiptypekey, so SPA-created records are
 * indistinguishable from RDS-synced ones at query time.
 *
 * Direction: edge goes FROM the CR object TO the Client (matches the
 * INCOMING traversal at src/services/queries/creditReportQueries.ts:228).
 */
async function createCreditReportClientEdge(
  fromKey: string,
  clientKey: string,
): Promise<void> {
  if (!fromKey || !clientKey) return;
  const apiClient = getGobodoClient();
  const relsApi = new GobodoApi.GraphRelationshipsApi(apiClient);
  const relTypeKey = getCreditReportClientRelKey();
  const payload = {
    fromKey,
    toKey: clientKey,
    key: `${fromKey}_${clientKey}${CUSTOM_SUFFIX}`,
    relationshiptypekey: relTypeKey,
  };
  try {
    await retryWithBackoff(() => relsApi.createRelationshipsIfAbsent([payload]), { maxRetries: 3 });
  } catch (err) {
    // Non-fatal: the record exists; the edge is what makes it appear in
    // the list. Surface the failure so callers can retry/log, but don't
    // unwind the createObject — partial state is recoverable.
    // eslint-disable-next-line no-console
    console.error('[arrayCreditReport] failed to create CR→Client edge:', err);
    throw err;
  }
}

/**
 * Idempotent backfill: ensure a Client → CreditReport edge exists for an
 * existing Array CR record.
 *
 * Why this is needed: records created before the graph-edge code shipped
 * (or any case where createRelationshipsIfAbsent was skipped/failed) have
 * a CR node in Gobodo but no edge to the Client, which means they don't
 * appear in the existing useCreditReportRecords list traversal.
 * createRelationshipsIfAbsent is a no-op when the edge already exists,
 * so calling this on every panel load is safe + cheap.
 */
export async function ensureClientEdge(record: Pick<ArrayCreditReportRecord, 'key' | 'clientId'>): Promise<void> {
  if (!record.key || !record.clientId) return;
  await createCreditReportClientEdge(record.key, record.clientId);
}

/**
 * Idempotent backfill of standard CR fields (Bureau, Pull Date, Product
 * Code, Status, Name, Report Key, Total Files Attached) on an Array CR
 * record. Records created before today's Pass 1 changes have the Array
 * metadata populated but the standard SF-style CR columns blank — which
 * means the row appears in the Credit Reports list but every column is
 * empty.
 *
 * No-op when the record already has Credit_Report_Product_Code__c set to
 * CR_ARRAY (the marker that signals a previous backfill or a fresh create
 * with the full field set).
 *
 * Returns true when a write happened, false when no-op.
 */
export async function backfillStandardFields(
  record: ArrayCreditReportRecord,
  clientName?: string,
): Promise<boolean> {
  if (!record.id) return false;

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const numericId = parseInt(record.id, 10);
  if (Number.isNaN(numericId)) return false;
  const existing = (await retryWithBackoff(() => objectsApi.getObjectById(numericId), {
    maxRetries: 3,
  })) as unknown as GobodoRecord;
  const existingProps = (existing.properties || existing.data || {}) as Record<string, unknown>;

  // Marker check — if Credit_Report_Product_Code__c is already CR_ARRAY,
  // this record was either freshly created today or backfilled previously.
  const currentProductCode = existingProps[FIELD_PRODUCT_CODE];
  if (currentProductCode === ARRAY_PRODUCT_CODE) return false;

  // Derive the credit-report status from the persisted Array statuses /
  // file IDs. If both files exist OR both Array statuses are Done →
  // success. If either is Failed → failure. Otherwise pending.
  const hasBothFiles = !!record.arrayPdfFileId && !!record.arrayJsonFileId;
  const bothDone = record.arrayPdfStatus === 'Done' && record.arrayJsonStatus === 'Done';
  const eitherFailed =
    record.arrayPdfStatus === 'Failed' || record.arrayJsonStatus === 'Failed';
  const derivedStatus = hasBothFiles || bothDone ? 'success' : eitherFailed ? 'failure' : 'pending';

  // Anchor to noon UTC so display in any timezone shows the same calendar
  // day. JavaScript's `new Date('2026-04-24')` treats date-only strings as
  // UTC midnight which shifts to the previous day in any negative-offset
  // timezone (e.g., PDT renders 2026-04-24 as "4/23 5pm").
  const pullDateOnly = (record.arrayFirstOrderedAt ?? record.requestedAt ?? new Date().toISOString()).slice(
    0,
    10,
  );
  const pullDate = `${pullDateOnly}T12:00:00Z`;

  const labelClientName =
    clientName ??
    (typeof existing.label === 'string'
      ? existing.label.replace(/^Array Credit Report\s*[—-]\s*/i, '').trim()
      : '');

  const properties: Record<string, unknown> = {
    [FIELD_PRODUCT_CODE]: ARRAY_PRODUCT_CODE, // also our backfill marker
    [FIELD_BUREAU]: ARRAY_BUREAU,
    [FIELD_PULL_DATE]: pullDate,
    [FIELD_STATUS]: derivedStatus,
    [FIELD_NAME]: existingProps[FIELD_NAME] ?? `Array — ${labelClientName} — ${pullDate}`,
    [FIELD_TOTAL_FILES]: (record.arrayPdfFileId ? 1 : 0) + (record.arrayJsonFileId ? 1 : 0),
    ...(record.arrayReportKey ? { [FIELD_REPORT_IDENTIFIER]: record.arrayReportKey } : {}),
  };

  // Strip null/empty (Gobodo's setProperty NPEs on nulls).
  const cleanProps: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (v != null && v !== '') cleanProps[k] = v;
  }

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = CREDIT_REPORT_OBJECT_KEY;
  payload.typeid = existing.typeid as unknown as number;
  payload.label = (existing.label as string | undefined) ?? '';
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = cleanProps;

  await retryWithBackoff(() => objectsApi.updateObject(numericId, payload), { maxRetries: 3 });
  return true;
}

export async function createArrayRequest(input: CreateRequestInput): Promise<ArrayCreditReportRecord> {
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const typeid = await getTypeId();
  const now = new Date().toISOString();
  const isApprovedAtCreation = !!input.approvedBy;
  const initialStatus: ArrayRequestStatus = isApprovedAtCreation ? 'approved' : 'pending_approval';

  // Initial approval history entry: the request itself. If the requester is
  // also an approver (auto-approved), record both events so the audit trail
  // shows the auto-approval.
  const initialHistory: ApprovalHistoryEntry[] = [
    {
      id: `evt-${now}-submitted`,
      action: 'submitted',
      approvedBy: input.requestedBy,
      approvalDate: now,
    },
    ...(isApprovedAtCreation
      ? [
          {
            id: `evt-${now}-approved`,
            action: 'auto_approved',
            approvedBy: input.approvedBy as string,
            approvalDate: now,
            notes: 'Approver self-pull (Credit Block Team Lead).',
          },
        ]
      : []),
  ];

  const properties: Record<string, unknown> = {
    // --- Standard CR fields (so the record shows up in the existing list
    // with populated columns, just like the legacy RDS-synced ones) ---
    [FIELD_NAME]: `Array — ${input.clientName} — ${now.slice(0, 10)}`,
    // Anchor to noon UTC so it displays as the same calendar day in any TZ.
    [FIELD_PULL_DATE]: `${now.slice(0, 10)}T12:00:00Z`,
    [FIELD_STATUS]: 'pending', // flips to success/failure in patchArrayRequest when statuses settle
    [FIELD_PRODUCT_CODE]: ARRAY_PRODUCT_CODE,
    [FIELD_BUREAU]: ARRAY_BUREAU,
    [FIELD_TOTAL_FILES]: 0, // updated by patchArrayRequest as files are saved
    // ClientName for fallback list lookups (the graph edge is the primary path).
    [f('ClientRelField')]: input.clientId,
    [f('AccountName')]: input.clientName,

    // --- Array provider + key ---
    'ArrayClientKey__c': input.arrayClientKey,

    // --- Workflow ---
    [f('ArrayRequestStatus')]: initialStatus,
    [f('ArrayRequestedBy')]: input.requestedBy,
    [f('ArrayRequestedAt')]: now,
    [f('ArrayApprovalHistory')]: JSON.stringify(initialHistory),
    ...(isApprovedAtCreation
      ? {
          [f('ArrayApprovedBy')]: input.approvedBy as string,
          [f('ArrayApprovedAt')]: now,
        }
      : {}),
  };

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = CREDIT_REPORT_OBJECT_KEY;
  payload.typeid = typeid as unknown as number;
  payload.label = `Array Credit Report — ${input.clientName}`;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = properties;

  const created = (await retryWithBackoff(() => objectsApi.createObject(payload), {
    maxRetries: 3,
  })) as unknown as GobodoRecord;
  const record = mapRecord(created);

  // Wire the graph edge so the new record shows up in the Client's CR list.
  // Best-effort: if this fails, the record still exists in Gobodo and a
  // subsequent retry can reconcile.
  await createCreditReportClientEdge(record.key, input.clientId).catch(() => {
    /* logged inside; non-fatal */
  });

  return record;
}

/** Patch an existing Array request with updated workflow or metadata fields.
 *
 * Two extra concerns beyond simple field updates:
 * 1. **Approval history append** — when `appendHistory` is provided, the
 *    existing `Array_Approval_History` JSON is read, the new entry pushed,
 *    and the whole array re-stringified. Atomic per call.
 * 2. **Standard-CR-field reflection** — when `arrayPdfStatus`/`arrayJsonStatus`
 *    or `arrayPdfFileId`/`arrayJsonFileId` change, we also touch
 *    `Credit_Report_Status__c` and `Total_Files_Attached__c` so the existing
 *    list view's columns stay accurate without the SPA running a separate
 *    derivation pass.
 */
export async function patchArrayRequest(
  id: string,
  updates: Partial<{
    requestStatus: ArrayRequestStatus;
    approvedBy: string;
    approvedAt: string;
    rejectionReason: string;
    arrayOrderId: string;
    arrayReportKey: string;
    arrayDisplayToken: string;
    arrayFirstOrderedAt: string;
    arrayPdfStatus: string;
    arrayJsonStatus: string;
    arrayPdfFileId: string;
    arrayJsonFileId: string;
  }>,
  options?: { appendHistory?: ApprovalHistoryEntry },
): Promise<ArrayCreditReportRecord> {
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const numericId = parseInt(id, 10);
  const existing = (await retryWithBackoff(() => objectsApi.getObjectById(numericId), {
    maxRetries: 3,
  })) as unknown as GobodoRecord;
  const existingProps = (existing.properties || existing.data || {}) as Record<string, unknown>;

  const fieldMap: Record<string, string> = {
    requestStatus: f('ArrayRequestStatus'),
    approvedBy: f('ArrayApprovedBy'),
    approvedAt: f('ArrayApprovedAt'),
    rejectionReason: f('ArrayRejectionReason'),
    arrayOrderId: f('ArrayOrderId'),
    arrayReportKey: f('ArrayReportKey'),
    arrayDisplayToken: f('ArrayDisplayToken'),
    arrayFirstOrderedAt: f('ArrayFirstOrderedAt'),
    arrayPdfStatus: f('ArrayPdfStatus'),
    arrayJsonStatus: f('ArrayJsonStatus'),
    arrayPdfFileId: f('ArrayPdfFileId'),
    arrayJsonFileId: f('ArrayJsonFileId'),
  };

  // Send ONLY the changed fields. Gobodo's updateObject accepts partial
  // properties maps and crashes (NullPointerException in setProperty) when
  // any provided value is null — so we must omit unset fields entirely
  // rather than merge the existing record's state.
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value == null || value === '') continue;
    const fieldName = fieldMap[key];
    if (!fieldName) continue;
    properties[fieldName] = value;
  }

  // Reflect Array reportKey onto the standard CreditReportIdentifier__c so
  // the existing list query picks it up as the report's stable identifier.
  if (updates.arrayReportKey) {
    properties[FIELD_REPORT_IDENTIFIER] = updates.arrayReportKey;
  }

  // When both PDF and JSON statuses settle to Done, mark the CR as success.
  // If either is Failed, mark failure. Otherwise leave Credit_Report_Status__c
  // alone (still 'pending' from create).
  const nextPdf = updates.arrayPdfStatus ?? (existingProps[f('ArrayPdfStatus')] as string | undefined);
  const nextJson = updates.arrayJsonStatus ?? (existingProps[f('ArrayJsonStatus')] as string | undefined);
  if (nextPdf === 'Done' && nextJson === 'Done') {
    properties[FIELD_STATUS] = 'success';
  } else if (nextPdf === 'Failed' || nextJson === 'Failed') {
    properties[FIELD_STATUS] = 'failure';
  }

  // Total_Files_Attached__c reflects how many of the two file slots are populated.
  const nextPdfFile = updates.arrayPdfFileId ?? (existingProps[f('ArrayPdfFileId')] as string | undefined);
  const nextJsonFile =
    updates.arrayJsonFileId ?? (existingProps[f('ArrayJsonFileId')] as string | undefined);
  if (updates.arrayPdfFileId !== undefined || updates.arrayJsonFileId !== undefined) {
    properties[FIELD_TOTAL_FILES] = (nextPdfFile ? 1 : 0) + (nextJsonFile ? 1 : 0);
  }

  // Append a new approval-history entry if requested.
  if (options?.appendHistory) {
    const existingHistoryRaw = existingProps[f('ArrayApprovalHistory')] as string | undefined;
    properties[f('ArrayApprovalHistory')] = appendApprovalHistory(existingHistoryRaw, options.appendHistory);
  }

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = CREDIT_REPORT_OBJECT_KEY;
  payload.typeid = existing.typeid as unknown as number;
  payload.label = (existing.label as string | undefined) ?? '';
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = properties;

  const updated = await retryWithBackoff(() => objectsApi.updateObject(numericId, payload), {
    maxRetries: 3,
  });
  return mapRecord(updated as unknown as GobodoRecord);
}

/** Fetch all Array credit-report records for a given client. */
export async function listForClient(clientId: string, arrayClientKey?: string): Promise<ArrayCreditReportRecord[]> {
  // Use the existing recordStorageService helper — it handles graph scoping,
  // pagination, and the Gobodo/RDS fallback in one place.
  const rawRecords = (await getRecordsForObject(CREDIT_REPORT_OBJECT_KEY)) as unknown as GobodoRecord[];
  const mapped = (rawRecords || []).map(mapRecord);
  return mapped.filter((r) => {
    if (r.clientId !== clientId && !r.clientId.startsWith(clientId)) return false;
    if (arrayClientKey && r.arrayClientKey && r.arrayClientKey !== arrayClientKey) return false;
    // Only Array-driven rows (others are legacy manual CR records)
    return !!r.arrayClientKey || !!r.requestStatus;
  });
}

/**
 * Fetch every Array credit-report record currently in 'pending_approval' state,
 * across all clients. Powers the F3 approver inbox (PendingArrayApprovalsPanel).
 * Same source as listForClient, just a different filter.
 */
export async function listAllPendingApprovals(): Promise<ArrayCreditReportRecord[]> {
  const rawRecords = (await getRecordsForObject(CREDIT_REPORT_OBJECT_KEY)) as unknown as GobodoRecord[];
  const mapped = (rawRecords || []).map(mapRecord);
  return mapped
    .filter((r) => r.requestStatus === 'pending_approval')
    // Skip orphaned records — pending_approval set but no client + requester
    // populated. These are test data / pre-Array-workflow leftovers; an
    // approver can't act on them anyway. Show only requests that have
    // identifiable context.
    .filter((r) => (r.arrayClientKey || r.clientId) && r.requestedBy)
    .sort((a, b) => (b.requestedAt ?? '').localeCompare(a.requestedAt ?? ''));
}

/**
 * The dedicated Keycloak role name for Array approvers — to be created in
 * Keycloak later (Q4 deferred). Kept as a forward-compatible check.
 */
export const ARRAY_APPROVER_ROLE = 'array_credit_report_approver';

/** Keycloak realm roles treated as approvers (fallback while ARRAY_APPROVER_ROLE isn't deployed). */
export const ARRAY_ADMIN_FALLBACK_ROLES = ['system_admin', 'System Admin', 'cb_supervisor'];

/**
 * Gobodo-resolved role names treated as Array approvers.
 *
 * Per the "How to pull 3B Credit Report" ops doc, approvers "are basically
 * credit block team leads" — so `Credit Block Team Lead` is the canonical
 * role. `System Administrator` is a superset that should also pass.
 */
export const ARRAY_APPROVER_GOBODO_ROLES = ['Credit Block Team Lead', 'System Administrator'];
